import type { Order, ApiOrder, InteractionHistory } from "@/components/orderList/types";

interface TransformOrdersParams {
  allOrders: ApiOrder[];
  currentUserUUID: string | undefined;
  unreadOrders: string[];
}

/**
 * Transform API orders to internal Order format
 */
export const transformOrders = ({
  allOrders,
  currentUserUUID,
  unreadOrders,
}: TransformOrdersParams): Order[] => {
  const now = new Date();

  return allOrders.map((order) => {
    const deadlineDate = new Date(`${order.process.deadline_datetime}Z`);
    const deadlineDifference = Math.round((deadlineDate.getTime() - now.getTime()) / 60000); // Convert to minutes

    let usersInteractions: Record<string, unknown[]> = {};
    try {
      const parsedData = JSON.parse(order.process.orderInteractionHistory);
      usersInteractions = parsedData.read || {};
    } catch {
      usersInteractions = {};
    }

    const userReadEntries = currentUserUUID ? usersInteractions[currentUserUUID] || [] : [];
    const hasUserRead = userReadEntries.length > 0;

    const isUnreadLocally = unreadOrders.includes(order.process.jobID);
    const hasUserMarkedAsUnread = hasUserRead && !isUnreadLocally;

    return {
      jobID: order.process.jobID,
      jobID_parent: order.process.jobID_parent || order.process.jobID,
      useCase_name: order.process.useCase_name,
      status: order.process.status,
      subject: order.process.subject,
      customer_name: order.order.metadata.customer_name?.value,
      customer_id: order.order.metadata.customer_id?.value,
      entity_name: order.order.metadata.entity_name?.value,
      entity_id: order.order.metadata.entity_id?.value || order.process.entity_id,
      order_delivery_date: order.order.metadata.order_delivery_date?.value,
      start_datetime: order.process.start_datetime,
      assignment: order.process.assignment?.assignment_name,
      assignment_id: order.process.assignment?.assignment_id,
      duplicate_job_ids: order.process.duplicate_job_ids || [],
      deadline: deadlineDifference,
      warnings:
        (order.process.steps?.metadata_warnings || 0) +
        (order.process.steps?.products_warnings || 0),
      products_counter: order.process.steps?.products_counter || 0,
      hasUserMarkedAsUnread,
      interactionHistory: order.process.orderInteractionHistory,
      ...(order.process.internal_note ? { internalNote: order.process.internal_note } : {}),
    };
  });
};

/**
 * Parse interaction history from string to object
 */
export const parseInteractionHistory = (
  interactionHistory: string | InteractionHistory
): InteractionHistory => {
  if (typeof interactionHistory === "string") {
    try {
      return JSON.parse(interactionHistory);
    } catch {
      return {};
    }
  }
  return interactionHistory;
};

/**
 * Get unique list of values from orders for a given field
 */
export const getUniqueListFromOrders = <T extends string | undefined>(
  orders: Order[],
  field: keyof Order
): Array<{ name: string; uid: string }> => {
  return Array.from(new Set(orders.map((order) => order[field] as T))).map((name) => ({
    name: name || "",
    uid: name || "",
  }));
};
